import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// 自訂 fetch，加入 20 秒逾時保護
const fetchWithTimeout = (...[url, options]: Parameters<typeof fetch>): ReturnType<typeof fetch> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  return fetch(url, { ...(options ?? {}), signal: controller.signal }).finally(() => clearTimeout(timer));
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  global: { fetch: fetchWithTimeout },
  auth: {
    persistSession: true,
    detectSessionInUrl: true,
    autoRefreshToken: true,
  },
});

// Auth helpers
export const authHelpers = {
  // 檢查帳號名稱是否可用
  async checkUsernameAvailable(username: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('username')
        .eq('username', username)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      // 如果沒有找到資料，表示帳號可用
      return !data;
    } catch (error) {
      console.error('Check username error:', error);
      throw error;
    }
  },

  // 使用帳號名稱登入（加入 15 秒超時 Promise.race）
  async signInWithUsername(username: string, password: string) {
    const loginPromise = async () => {
      const cleanUsername = username.trim().toLowerCase();

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('email')
        .eq('username', cleanUsername)
        .maybeSingle();

      if (profileError) throw profileError;

      if (!profile || !profile.email) {
        throw new Error('帳號不存在');
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: profile.email,
        password,
      });

      if (error) throw error;
      return data;
    };

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('TIMEOUT')), 15000)
    );

    try {
      return await Promise.race([loginPromise(), timeoutPromise]);
    } catch (error) {
      console.error('SignIn with username error:', error);
      throw error;
    }
  },

  // 註冊新用戶
  async signUp(email: string, password: string, userData: { name: string; phone: string; username?: string }) {
    try {
      // 如果有提供 username，先檢查是否可用
      if (userData.username) {
        const isAvailable = await this.checkUsernameAvailable(userData.username);
        if (!isAvailable) {
          throw new Error('此帳號名稱已被使用');
        }
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: userData.name,
            phone: userData.phone,
            username: userData.username,
          },
          emailRedirectTo: `${window.location.origin}/member`
        }
      });

      if (authError) {
        console.error('Auth error:', authError);
        throw authError;
      }

      if (!authData.user) {
        throw new Error('註冊失敗：無法建立用戶');
      }

      // 如果有 username，更新到 profiles（profile 由資料庫觸發器自動建立）
      if (userData.username && authData.user.id) {
        // 等待一下讓觸發器完成
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ username: userData.username, email })
          .eq('id', authData.user.id);

        if (updateError) {
          console.error('Update username error:', updateError);
          // 不拋出錯誤，因為註冊已成功，username 可以之後再設定
        }
      }

      return { ...authData, needsEmailConfirmation: !authData.user.email_confirmed_at };
    } catch (error) {
      console.error('SignUp error:', error);
      throw error;
    }
  },

  // 登入
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return data;
  },

  // Google 登入
  async signInWithGoogle() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/login`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });

    if (error) throw error;
    return data;
  },

  // Facebook 登入
  async signInWithFacebook() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: {
        redirectTo: `${window.location.origin}/login`,
      },
    });

    if (error) throw error;
    return data;
  },

  // 登出
  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  // 獲取當前用戶（使用 getUser 向伺服器驗證 token，確保 session 有效）
  async getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    return user ?? null;
  },

  // 獲取當前 Session
  async getSession() {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  },

  // 監聽 Auth 狀態變化
  onAuthStateChange(callback: (event: string, session: any) => void) {
    return supabase.auth.onAuthStateChange(callback);
  },

  // 獲取用戶資料
  async getUserProfile(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  // 更新用戶資料
  async updateUserProfile(userId: string, updates: any) {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  // 同步 OAuth 用戶資料到 profiles
  async syncOAuthProfile(user: any) {
    try {
      // 檢查 profile 是否已存在
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      const profileData = {
        id: user.id,
        email: user.email,
        name: user.user_metadata?.full_name || user.user_metadata?.name || existingProfile?.name || '',
        avatar_url: user.user_metadata?.avatar_url || user.user_metadata?.picture || existingProfile?.avatar_url || null,
        provider: user.app_metadata?.provider || 'email',
      };

      if (existingProfile) {
        // 更新現有 profile
        const { error } = await supabase
          .from('profiles')
          .update({
            name: profileData.name,
            avatar_url: profileData.avatar_url,
            provider: profileData.provider,
          })
          .eq('id', user.id);

        if (error) throw error;
      } else {
        // 建立新 profile
        const { error } = await supabase
          .from('profiles')
          .insert(profileData);

        if (error && !error.message.includes('duplicate key')) {
          throw error;
        }
      }

      return profileData;
    } catch (error) {
      console.error('Sync OAuth profile error:', error);
      throw error;
    }
  },

  // 更新密碼
  async updatePassword(newPassword: string) {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) throw error;
    return data;
  },
};

// 訂單相關
export const orderHelpers = {
  // 獲取用戶訂單
  async getUserOrders(userId: string) {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // 建立訂單
  async createOrder(userId: string, orderData: any) {
    const { data, error } = await supabase
      .from('orders')
      .insert({
        id: `ORD${Date.now()}`,
        user_id: userId,
        order_date: new Date().toISOString().split('T')[0],
        total: orderData.total,
        status: orderData.status || '處理中',
        items: orderData.items,
      })
      .select()
      .maybeSingle();

    if (error) throw error;
    return data;
  },
};

// 預約相關
export const bookingHelpers = {
  // 獲取用戶預約
  async getUserBookings(userId: string) {
    const { data, error } = await supabase
      .from('bookings')
      .select('*')
      .eq('user_id', userId)
      .order('course_date', { ascending: false });

    if (error) throw error;
    return data;
  },

  // 建立預約
  async createBooking(userId: string, bookingData: any) {
    const { data, error } = await supabase
      .from('bookings')
      .insert({
        user_id: userId,
        course_name: bookingData.courseName,
        course_date: bookingData.date,
        course_time: bookingData.time,
        instructor: bookingData.instructor || '',
        status: '已預約',
        fragrance_name: bookingData.fragranceName || null,
        fragrance_ratio: bookingData.fragranceRatio || null,
        notes: bookingData.notes || null,
        phone: bookingData.phone || null,
      })
      .select()
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  // 更新預約狀態
  async updateBookingStatus(bookingId: number, status: string) {
    const { data, error } = await supabase
      .from('bookings')
      .update({ status })
      .eq('id', bookingId)
      .select()
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  // 更新香氛記錄
  async updateBookingFragrance(bookingId: number, fragranceName: string, fragranceRatio: string) {
    const { data, error } = await supabase
      .from('bookings')
      .update({
        fragrance_name: fragranceName,
        fragrance_ratio: fragranceRatio,
      })
      .eq('id', bookingId)
      .select()
      .maybeSingle();

    return { data, error };
  },
};

// 課程相關
export const courseHelpers = {
  // 獲取所有課程
  async getAllCourses() {
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data;
  },

  // 獲取單一課程
  async getCourse(courseId: number) {
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .eq('id', courseId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },
};